import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Grid, Box, Container, Typography, Button } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Sound, Movie, File, MultiLineBody } from '../../utils/util';
import { CONFIG } from '../../states/settings';
import { api_menu_list } from '../../states/selector';
import UnifiedArchive from '../../blocks/unified-archive';

const UnifiedArchiveList = () => {
  const { year, month } = useParams();
  const location = useLocation();
  const menuList = useRecoilValue(api_menu_list);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!year || !month || !menuList || menuList.length === 0) return;

    (async () => {
      try {
        // console.log('UnifiedArchiveList: パラメータ', { year, month });
        // console.log('UnifiedArchiveList: メニューリスト', menuList);
        
        const allItems = [];
        
        // 各メニューの記事を取得して統合
        for (const menu of menuList) {
          try {
            const url = CONFIG.API.INFO + menu.json_file.split('.')[0] + CONFIG.API.INFO_EX;
            const response = await fetch(url);
            
            if (!response.ok) {
              console.warn(`メニュー ${menu.group_name} のHTTPエラー:`, response.status, response.statusText);
              continue;
            }
            
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              console.warn(`メニュー ${menu.group_name} のContent-Typeエラー:`, contentType);
              continue;
            }
            
            const json = await response.json();
            const messages = json.messages || [];
            
            // 各記事にgroup_nameを追加
            const itemsWithGroup = messages.map(item => ({
              ...item,
              group_name: menu.group_name
            }));
            
            allItems.push(...itemsWithGroup);
          } catch (error) {
            console.error(`メニュー ${menu.group_name} のデータ取得に失敗:`, error);
          }
        }

        // console.log('UnifiedArchiveList: 全記事数', allItems.length);
        setItems(allItems);

        // 指定された年月の記事をフィルタリング
        const targetYear = parseInt(year);
        const targetMonth = parseInt(month);
        
        // console.log('UnifiedArchiveList: フィルタ条件', { targetYear, targetMonth });
        
        const filtered = allItems.filter(item => {
          const itemDate = new Date(item.sent_at);
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1;
          
          return itemYear === targetYear && itemMonth === targetMonth;
        });

        // 日付の降順でソート（新しい順）
        const sorted = filtered.sort((a, b) => {
          const dateA = new Date(a.sent_at);
          const dateB = new Date(b.sent_at);
          return dateB - dateA; // 降順（新しい順）
        });

        // console.log('UnifiedArchiveList: フィルタ結果', filtered.length);
        // console.log('UnifiedArchiveList: ソート結果', sorted.length);
        setFilteredItems(sorted);
        setLoading(false);
      } catch (error) {
        console.error('統一アーカイブデータの取得に失敗しました:', error);
        setLoading(false);
      }
    })();
  }, [year, month, menuList]);

  // スクロール位置の復元
  useEffect(() => {
    const scrollPosition = location.state?.scroll_position;
    if (scrollPosition && filteredItems.length > 0) {
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 200);
    }
  }, [filteredItems.length, location.state]);

  if (loading) {
    return <div>読み込み中...</div>;
  }

  return (
    <Box className='wrapper' sx={{
      '@media (max-width: 900px)':{
        padding: 2
      },
      padding: 3
    }}>
      <Box 
        mb={3}
        sx={{
          position: 'fixed',
          top: '64px',
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: '#ffffff',
          padding: '16px 24px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '@media (max-width: 900px)': {
            padding: '16px 16px'
          }
        }}
      >
        <Link to={`${CONFIG.PAGE_ROOT_PATH}/`} style={{ textDecoration: 'none' }}>
          <Button 
            startIcon={<ArrowBackRoundedIcon />}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Topページに戻る
          </Button>
        </Link>
        
        <Typography variant="h4" component="h1" gutterBottom>
          {year}年{month}月の記事一覧
        </Typography>
        
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {filteredItems.length}件の記事
        </Typography>
      </Box>

      <Box sx={{ marginTop: '140px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={8} xl={8}>
            <Grid container spacing={2}>
              {filteredItems.map((item) => (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} key={`${item.group_name}-${item.id}`}>
            <Box
              className={`item_wrapper ${item.is_emergency ? 'emergency' : ''}`}
              pt='.7rem' pr='.7rem' pl='.7rem' pb='1.4rem'
              sx={{
                boxShadow: 2, 
                bgcolor: '#fafafa', 
                alignItems: 'stretch',
                borderRadius: 2,
                border: '1px solid transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(33, 150, 243, 0.2)',
                  border: '1px solid #2196f3',
                  '& .sender_name': {
                    color: '#1976d2',
                  },
                  '& .time_c1': {
                    color: '#1976d2',
                  },
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: '1rem' }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mr: 1, 
                    color: '#1976d2',
                    fontWeight: 'bold',
                    backgroundColor: '#e3f2fd',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75rem'
                  }}
                >
                  {item.group_name}
                </Typography>
                <Link to={{
                  pathname: `${CONFIG.PAGE_ROOT_PATH}/${item.group_name}/items/${item.id}`,
                  state: {
                    from_url: location.pathname,
                    archive_type: 'unified',
                    scroll_position: window.scrollY
                  }
                }} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Typography component="h4" variant="h6" fontWeight='600' sx={{
                    lineHeight:'1.4',
                    cursor: 'pointer',
                    position: 'relative',
                    padding: '8px 32px 8px 12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    border: '1px solid rgba(25, 118, 210, 0.2)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    wordBreak: 'break-word',
                    '&:hover': {
                      color: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      border: '1px solid #1976d2',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&::after': {
                        content: '"👆"',
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '14px',
                        opacity: 0.7,
                        pointerEvents: 'none'
                      }
                    },
                    '&::before': {
                      content: '"📖"',
                      marginRight: '8px',
                      fontSize: '16px'
                    }
                  }}>
                    {item.title}
                  </Typography>
                </Link>
              </Box>

              <Grid className='item_list_other' container mb='1.5rem'>
                <p className='sender_name'>{item.sender_name}</p>
                <time className='time_c1' dateTime={format(new Date(item.sent_at),'yyyy-MM-dd HH:mm')}>
                  {format(new Date(item.sent_at),'yyyy-MM-dd HH:mm')}
                </time>
              </Grid>

              <div onClick={(e) => e.stopPropagation()}>
                <Sound item={item.sound_file ? item.sound_file : (item.generated_sound_file ? item.generated_sound_file : null)} />
              </div>

              <Typography className='mabiku' component="p" variant="h7" mb='1.2rem' sx={{ 
                fontSize: '.9rem',
                lineHeight: '1.6'
              }}>
                <MultiLineBody body={item.body} />
              </Typography>

              <div onClick={(e) => e.stopPropagation()}>
                <Movie item={item.movie_url} />
                <File item={item.file} />
              </div>
            </Box>
          </Grid>
              ))}
            </Grid>
          </Grid>
          
          <Grid item xs={12} md={4} lg={4} xl={4}>
            <UnifiedArchive />
          </Grid>
        </Grid>
      </Box>

      {filteredItems.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            該当月の記事はありません
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UnifiedArchiveList;
